import React, { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, List, ListItemButton, ListItemText,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import {
  Settings,
  Plus,
  Pencil,
  Trash2,
  RotateCcw,
  LayoutGrid,
  CheckSquare,
  Sliders,
  Flag,
  Link2,
  FileSpreadsheet,
  Shield,
  Users,
  Bell,
  Bot,
  Zap,
  FolderKanban,
} from "lucide-react";
import { GlassCard, PageHeader, PersonAvatar, T, TYPE, formatRelative, ghostButtonSx, labelSx, primaryButtonSx } from "../user/complaints/shared";
import { pm, errorMessage } from "./api";
import { PmPage, usePm } from "./PmContext";
import { ProjectBadge, TypeIcon } from "./ui";
import { MultiSelect, PersonPicker, Select, fieldSx } from "./inputs";
import { AutomationRules } from "./project/ProjectSettings";

const Section = ({ title, subtitle, action, children }) => (
  <GlassCard sx={{ p: 2.5, mb: 2.5 }}>
    <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={{ flexGrow: 1 }}><Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle }}>{title}</Typography>{subtitle && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{subtitle}</Typography>}</Box>
      {action}
    </Stack>
    {children}
  </GlassCard>
);

/**
 * Generic list editor for config collections.
 * fields: [{ key, label, type: text|color|number|bool|select|multiselect|options, options, create?: only on create }]
 */
const ConfigList = ({ path, title, subtitle, columns, fields, defaults = {}, canEdit, noCreate = false, transform = (x) => x }) => {
  const ctx = usePm();
  const api = pm.config(path);
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.list().then(setRows).catch((err) => setError(errorMessage(err)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  const save = async () => {
    try {
      const body = transform(Object.fromEntries(fields.filter((f) => !(edit._id && f.create)).map((f) => [f.key, edit[f.key]])), edit);
      if (edit._id) await api.update(edit._id, body);
      else await api.create(body);
      setEdit(null);
      load();
      ctx.reload();
      ctx.notify("Saved");
    } catch (err) {
      ctx.fail(err);
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name || r.label}"?`)) return;
    try {
      await api.remove(r._id);
      load();
      ctx.reload();
    } catch (err) {
      ctx.fail(err);
    }
  };
  return (
    <Section title={title} subtitle={subtitle} action={canEdit && !noCreate && <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Plus size={13} />} onClick={() => setEdit({ ...defaults })}>Add</Button>}>
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>{columns.map(([k, l]) => <TableCell key={k} sx={labelSx}>{l}</TableCell>)}<TableCell /></TableRow></TableHead>
          <TableBody>
            {(rows || []).map((r) => (
              <TableRow key={r._id} sx={{ opacity: r.isActive === false ? 0.5 : 1 }}>
                {columns.map(([k, , fmt]) => <TableCell key={k} sx={{ fontSize: TYPE.small }}>{fmt ? fmt(r[k], r, ctx) : String(r[k] ?? "—")}</TableCell>)}
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {canEdit && <IconButton size="small" onClick={() => setEdit(JSON.parse(JSON.stringify(r)))}><Pencil size={14} /></IconButton>}
                  {canEdit && !r.isSystem && <IconButton size="small" onClick={() => remove(r)}><Trash2 size={14} /></IconButton>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{edit?._id ? "Edit" : "Add"} · {title}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <Stack spacing={2}>
              {fields.filter((f) => !f.hidden?.(edit)).map((f) => {
                const v = edit[f.key];
                const set = (val) => setEdit((e) => ({ ...e, [f.key]: val }));
                const disabled = Boolean(edit._id && f.create);
                if (f.type === "bool") return <FormControlLabel key={f.key} control={<Switch checked={Boolean(v)} onChange={(e) => set(e.target.checked)} />} label={f.label} />;
                if (f.type === "select") return <Select key={f.key} label={f.label} value={v} onChange={set} options={typeof f.options === "function" ? f.options(ctx, edit) : f.options} disabled={disabled} allowEmpty={f.allowEmpty} />;
                if (f.type === "multiselect") return <MultiSelect key={f.key} label={f.label} value={(v || []).map(String)} onChange={set} options={f.options(ctx, edit)} />;
                if (f.type === "options") return <TextField key={f.key} size="small" label={f.label} helperText="One per line" multiline minRows={3} value={(v || []).join("\n")} onChange={(e) => set(e.target.value.split("\n"))} sx={fieldSx} />;
                return <TextField key={f.key} size="small" type={f.type === "color" ? "color" : f.type === "number" ? "number" : "text"} label={f.label} value={v ?? ""} disabled={disabled} helperText={f.help}
                  onChange={(e) => set(f.type === "number" ? Number(e.target.value) : e.target.value)} sx={fieldSx} />;
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button></DialogActions>
      </Dialog>
    </Section>
  );
};

const Swatch = (c) => <Box sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: c }} />;
const bool = (v) => (v === false ? "No" : "Yes");

// ---------------------------------------------------------------------------
const Roles = ({ canEdit }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [edit, setEdit] = useState(null);
  const api = pm.config("roles");
  const load = () => api.list().then(setRows).catch(() => {});
  useEffect(() => {
    load();
    pm.permissionCatalogue().then(setCatalogue).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!catalogue) return null;
  const locked = (r) => ["owner", "ws_admin"].includes(r.key);
  const save = async () => {
    try {
      const body = { name: edit.name, description: edit.description, permissions: edit.permissions, ...(edit._id ? {} : { scope: edit.scope }) };
      if (locked(edit)) delete body.permissions;
      if (edit._id) await api.update(edit._id, body);
      else await api.create(body);
      setEdit(null);
      load();
      ctx.reload();
      ctx.notify("Role saved");
    } catch (err) {
      ctx.fail(err);
    }
  };
  const groups = (scope) => {
    const list = catalogue[scope];
    const by = {};
    for (const p of list) (by[p.split(".")[0]] = by[p.split(".")[0]] || []).push(p);
    return by;
  };
  return (
    <Section title="Roles & permissions" subtitle="Workspace roles decide who can create projects or administer PM. Project roles decide what members can do inside a project. Changes apply immediately."
      action={canEdit && <Stack direction="row" spacing={1}><Button size="small" onClick={() => setEdit({ scope: "project", name: "", permissions: ["project.view", "issue.view"] })}>+ Project role</Button><Button size="small" onClick={() => setEdit({ scope: "workspace", name: "", permissions: ["project.browse"] })}>+ Workspace role</Button></Stack>}>
      {["workspace", "project"].map((scope) => (
        <Box key={scope} sx={{ mb: 2 }}>
          <Typography sx={{ ...labelSx, mb: 1 }}>{scope} roles</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {rows.filter((r) => r.scope === scope).map((r) => (
              <Chip key={r._id} label={`${r.name} · ${locked(r) ? "all" : r.permissions.length}`} onClick={() => setEdit(JSON.parse(JSON.stringify(r)))} onDelete={canEdit && !r.isSystem ? async () => { if (window.confirm(`Delete role ${r.name}?`)) { try { await api.remove(r._id); load(); } catch (err) { ctx.fail(err); } } } : undefined} />
            ))}
          </Stack>
        </Box>
      ))}
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{edit?._id ? edit.name : `New ${edit?.scope} role`}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField size="small" label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} sx={fieldSx} disabled={!canEdit} />
                <TextField size="small" fullWidth label="Description" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} sx={fieldSx} disabled={!canEdit} />
              </Stack>
              {locked(edit) ? <Alert severity="info">This built-in role always has every permission.</Alert> : Object.entries(groups(edit.scope)).map(([g, perms]) => (
                <Box key={g} sx={{ mb: 1.5 }}>
                  <Typography sx={{ ...labelSx, fontSize: "0.66rem" }}>{g}</Typography>
                  <Stack direction="row" flexWrap="wrap">
                    {perms.map((p) => (
                      <FormControlLabel key={p} sx={{ minWidth: 200 }} control={<Checkbox size="small" disabled={!canEdit} checked={edit.permissions.includes(p)} onChange={(e) => setEdit({ ...edit, permissions: e.target.checked ? [...edit.permissions, p] : edit.permissions.filter((x) => x !== p) })} />}
                        label={<Typography sx={{ fontSize: TYPE.small }}>{p.split(".").slice(1).join(".") || p}</Typography>} />
                    ))}
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>Close</Button>{canEdit && <Button variant="contained" sx={primaryButtonSx} disabled={!edit?.name} onClick={save}>Save role</Button>}</DialogActions>
      </Dialog>
    </Section>
  );
};

const Access = ({ isAdmin }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const load = () => pm.access().then(setRows).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  return (
    <Section title="People" subtitle="Each person's workspace PM role and who they report to. The workspace owner is always PM Admin.">
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>{["Person", "Workspace role", "Reports to", "Access"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={String(p.id)}>
                <TableCell><Stack direction="row" spacing={1} alignItems="center"><PersonAvatar name={p.name} size={26} /><Box><Typography sx={{ fontSize: TYPE.small }}>{p.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p.email}</Typography></Box></Stack></TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  {p.kind === "owner" ? <Chip size="small" label="Owner · PM Admin" /> : (
                    <Select value={p.roleId ? String(p.roleId) : ""} disabled={!isAdmin} onChange={async (roleId) => { try { await pm.updateAccess(p.id, { roleId }); load(); } catch (err) { ctx.fail(err); } }}
                      options={ctx.workspaceRoles.map((r) => ({ value: String(r._id), label: r.name }))} />
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  {p.kind === "owner" ? <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>—</Typography> : (
                    <PersonPicker label="" value={p.leader?.id ? String(p.leader.id) : null} disabled={!isAdmin} people={ctx.people.filter((x) => String(x.id) !== String(p.id))}
                      onChange={async (leaderId) => { try { await pm.updateAccess(p.id, { leaderId }); ctx.notify(leaderId ? "Leader assigned" : "Leader removed"); load(); } catch (err) { ctx.fail(err); } }} />
                  )}
                </TableCell>
                <TableCell>{p.kind !== "owner" && <Switch size="small" disabled={!isAdmin} checked={p.isActive !== false} onChange={async (e) => { try { await pm.updateAccess(p.id, { isActive: e.target.checked }); load(); } catch (err) { ctx.fail(err); } }} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

const General = ({ settings, save, canEdit }) => {
  const ctx = usePm();
  const [f, setF] = useState(settings);
  useEffect(() => setF(settings), [settings]);
  const lines = (k) => (f[k] || []).join("\n");
  return (
    <Section title="General" action={canEdit && <Button variant="contained" sx={primaryButtonSx} onClick={() => save({ projectStatuses: f.projectStatuses, resolutions: f.resolutions, nonMemberRoleKey: f.nonMemberRoleKey, teamMemberRoleKey: f.teamMemberRoleKey, allowInternalVisibility: f.allowInternalVisibility, clone: f.clone, attachments: f.attachments })}>Save</Button>}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><TextField fullWidth multiline minRows={4} size="small" label="Project statuses (one per line)" value={lines("projectStatuses")} onChange={(e) => setF({ ...f, projectStatuses: e.target.value.split("\n") })} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth multiline minRows={4} size="small" label="Resolutions (one per line)" value={lines("resolutions")} onChange={(e) => setF({ ...f, resolutions: e.target.value.split("\n") })} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={6}><Select label="Role for non-members of shared/internal projects" value={f.nonMemberRoleKey} onChange={(v) => setF({ ...f, nonMemberRoleKey: v })} options={ctx.projectRoles.map((r) => ({ value: r.key || r._id, label: r.name }))} /></Grid>
        <Grid item xs={12} sm={6}><Select label="Role for team members of team projects" value={f.teamMemberRoleKey} onChange={(v) => setF({ ...f, teamMemberRoleKey: v })} options={ctx.projectRoles.map((r) => ({ value: r.key || r._id, label: r.name }))} /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={Boolean(f.allowInternalVisibility)} onChange={(e) => setF({ ...f, allowInternalVisibility: e.target.checked })} />} label="Allow “internal” project visibility" /></Grid>
        <Grid item xs={12}>
          <Typography sx={{ ...labelSx, mb: 0.5 }}>When cloning an issue, also copy</Typography>
          {["attachments", "comments", "subtasks"].map((k) => <FormControlLabel key={k} control={<Checkbox size="small" checked={Boolean(f.clone?.[k])} onChange={(e) => setF({ ...f, clone: { ...f.clone, [k]: e.target.checked } })} />} label={k} />)}
        </Grid>
        <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="number" label="Max file size (MB)" value={f.attachments?.maxFileSizeMb || 25} onChange={(e) => setF({ ...f, attachments: { ...f.attachments, maxFileSizeMb: Number(e.target.value) } })} sx={fieldSx} /></Grid>
        <Grid item xs={12} sm={9}><TextField fullWidth size="small" label="Allowed file extensions" value={(f.attachments?.allowedExtensions || []).join(", ")} onChange={(e) => setF({ ...f, attachments: { ...f.attachments, allowedExtensions: e.target.value.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean) } })} sx={fieldSx} /></Grid>
      </Grid>
    </Section>
  );
};

const EVENT_LABEL = {
  "issue.assigned": "Assigned to you", "issue.mentioned": "Mentioned", "issue.commented": "New comment", "issue.status_changed": "Status changed", "issue.priority_changed": "Priority changed",
  "issue.updated": "Issue updated", "sprint.started": "Sprint started", "sprint.completed": "Sprint completed", "issue.due": "Due soon", "issue.overdue": "Overdue",
  "release.changed": "Release changes", "project.membership": "Added to a project",
};
const Notifications = ({ settings, save, canEdit }) => {
  const [events, setEvents] = useState(settings.notifications.events);
  const [hours, setHours] = useState(settings.notifications.dueSoonHours);
  const [tpl, setTpl] = useState(settings.emailTemplates);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    setEvents(settings.notifications.events);
    setHours(settings.notifications.dueSoonHours);
    setTpl(settings.emailTemplates);
  }, [settings]);
  return (
    <>
      <Section title="Notifications" subtitle="Which events notify in the app and by email. Members can further limit this per project (all / participating / mentions / none)."
        action={canEdit && <Button variant="contained" sx={primaryButtonSx} onClick={() => save({ notifications: { events, dueSoonHours: hours } })}>Save</Button>}>
        <Table size="small">
          <TableHead><TableRow>{["Event", "In-app", "Email"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {Object.entries(events).map(([ev, c]) => (
              <TableRow key={ev}>
                <TableCell sx={{ fontSize: TYPE.small }}>{EVENT_LABEL[ev] || ev}</TableCell>
                <TableCell><Switch size="small" checked={c.inapp !== false} onChange={(e) => setEvents({ ...events, [ev]: { ...c, inapp: e.target.checked } })} /></TableCell>
                <TableCell><Switch size="small" checked={Boolean(c.email)} onChange={(e) => setEvents({ ...events, [ev]: { ...c, email: e.target.checked } })} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TextField size="small" type="number" label="“Due soon” window (hours)" value={hours} onChange={(e) => setHours(Number(e.target.value))} sx={{ ...fieldSx, mt: 2, width: 220 }} />
      </Section>
      <Section title="Email templates" subtitle={`Variables: ${settings.templateVariables.map((v) => `{{${v}}}`).join(" ")}`}
        action={canEdit && <Button variant="contained" sx={primaryButtonSx} onClick={() => save({ emailTemplates: tpl })}>Save templates</Button>}>
        <List dense>
          {Object.entries(tpl).map(([ev, t]) => (
            <Box key={ev} sx={{ borderBottom: `1px solid ${T.border}` }}>
              <ListItemButton onClick={() => setOpen(open === ev ? null : ev)}><ListItemText primary={ev} secondary={t.subject} /><Switch size="small" checked={t.enabled !== false} onClick={(e) => e.stopPropagation()} onChange={(e) => setTpl({ ...tpl, [ev]: { ...t, enabled: e.target.checked } })} /></ListItemButton>
              {open === ev && (
                <Stack spacing={1.5} sx={{ p: 2 }}>
                  <TextField size="small" label="Subject" value={t.subject} onChange={(e) => setTpl({ ...tpl, [ev]: { ...t, subject: e.target.value } })} sx={fieldSx} />
                  <TextField size="small" multiline minRows={5} label="Body" value={t.body} onChange={(e) => setTpl({ ...tpl, [ev]: { ...t, body: e.target.value } })} sx={fieldSx} />
                </Stack>
              )}
            </Box>
          ))}
        </List>
      </Section>
    </>
  );
};

const HealthAi = ({ settings, save, canEdit }) => {
  const [h, setH] = useState(settings.health);
  const [ai, setAi] = useState(settings.ai);
  useEffect(() => {
    setH(settings.health);
    setAi(settings.ai);
  }, [settings]);
  const fields = [["overduePctAmber", "Overdue % → amber"], ["overduePctRed", "Overdue % → red"], ["blockedAmber", "Blocked issues → amber"], ["blockedRed", "Blocked issues → red"], ["unassignedPctAmber", "Unassigned % → amber"], ["unassignedPctRed", "Unassigned % → red"], ["sprintCompletionAmber", "Sprint pace ≤ % → amber"], ["sprintCompletionRed", "Sprint pace ≤ % → red"]];
  return (
    <>
      <Section title="Project health thresholds" subtitle="Health is computed only from these measured indicators — nothing is estimated." action={canEdit && <Button variant="contained" sx={primaryButtonSx} onClick={() => save({ health: h })}>Save</Button>}>
        <Grid container spacing={2}>{fields.map(([k, l]) => <Grid item xs={6} sm={3} key={k}><TextField fullWidth size="small" type="number" label={l} value={h[k]} onChange={(e) => setH({ ...h, [k]: Number(e.target.value) })} sx={fieldSx} /></Grid>)}</Grid>
      </Section>
      <Section title="AI project assistant" subtitle="Summaries, requirement breakdowns, sub-task and acceptance-criteria suggestions, release notes. AI never changes data by itself — people apply suggestions." action={canEdit && <Button variant="contained" sx={primaryButtonSx} onClick={() => save({ ai })}>Save</Button>}>
        {!settings.aiProviderConfigured && <Alert severity="warning" sx={{ mb: 1.5 }}>The AI provider (Azure OpenAI) is not configured on the server, so AI features stay unavailable even when enabled.</Alert>}
        <FormControlLabel control={<Switch checked={Boolean(ai.enabled)} onChange={(e) => setAi({ ...ai, enabled: e.target.checked })} />} label="Enable the AI assistant" />
      </Section>
    </>
  );
};

/** Soft-deleted projects (PM admins only, like the restore endpoint). Deleted issues live in each project's settings. */
const Trash = () => {
  const ctx = usePm();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [restored, setRestored] = useState([]);
  const load = () => pm.trashedProjects().then(setRows).catch((err) => { setRows([]); ctx.fail(err); });
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (p) => {
    setBusy(p._id);
    try {
      await pm.restoreProject(p._id);
      ctx.notify(`${p.key} restored`);
      setRestored((r) => [p, ...r]);
      await load();
    } catch (err) {
      ctx.fail(err, "Could not restore");
    } finally {
      setBusy(null);
    }
  };
  return (
    <Section title="Trash" subtitle="Deleted projects keep all their issues, sprints and files until restored. Deleted issues are restored from each project's Settings → Trash.">
      {restored.length > 0 && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          Restored: {restored.map((p, i) => <React.Fragment key={p._id}>{i > 0 && ", "}<RouterLink to={`/projects/${p.key}`}>{p.key}</RouterLink></React.Fragment>)}
        </Alert>
      )}
      {rows === null && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Loading…</Typography>}
      {rows && !rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>The trash is empty.</Typography>}
      {rows && rows.length > 0 && (
        <Table size="small">
          <TableHead><TableRow>{["Project", "Issues", "Deleted", "By", ""].map((h, i) => <TableCell key={i} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p._id}>
                <TableCell>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <ProjectBadge project={p} size={26} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{p.name}</Typography>
                      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p.key}{p.archivedAt ? " · archived" : ""}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: TYPE.small }}>{p.issueCount}</TableCell>
                <TableCell sx={{ fontSize: TYPE.small, whiteSpace: "nowrap" }}>{formatRelative(p.deletedAt)}</TableCell>
                <TableCell sx={{ fontSize: TYPE.small }}>{p.deletedBy?.name || "—"}</TableCell>
                <TableCell align="right"><Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<RotateCcw size={14} />} disabled={busy === p._id} onClick={() => restore(p)}>Restore</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const PM_SETTING_GROUPS = [
  {
    title: "PROJECT CONFIGURATION",
    items: [
      { key: "general", label: "General", desc: "Project defaults, attachment limits and cloning rules", icon: Settings },
      { key: "projectTypes", label: "Project types", desc: "Categories and classifications for workspace projects", icon: FolderKanban },
      { key: "templates", label: "Project templates", desc: "Starter blueprints and default issue structures for new projects", icon: LayoutGrid },
    ],
  },
  {
    title: "ISSUES & WORKFLOW",
    items: [
      { key: "types", label: "Issue types", desc: "Configure hierarchy, epics, stories, tasks, and bugs", icon: CheckSquare },
      { key: "statuses", label: "Statuses", desc: "Manage workflow stages across To do, In progress, and Done", icon: Sliders },
      { key: "priorities", label: "Priorities", desc: "Urgency levels and default assignments for new issues", icon: Flag },
      { key: "links", label: "Link types", desc: "Issue relationship blockers, dependencies and associations", icon: Link2 },
      { key: "fields", label: "Custom fields", desc: "Extra data fields scoped to projects and issue types", icon: FileSpreadsheet },
    ],
  },
  {
    title: "PEOPLE & PERMISSIONS",
    items: [
      { key: "roles", label: "Roles & permissions", desc: "Control workspace administration and project access privileges", icon: Shield },
      { key: "people", label: "People access", desc: "Manage member roles and PM access status across workspace", icon: Users },
    ],
  },
  {
    title: "NOTIFICATIONS & AI",
    items: [
      { key: "notifications", label: "Notifications & email", desc: "In-app and email event notifications and templates", icon: Bell },
      { key: "health", label: "Health & AI", desc: "Threshold indicators for project health and AI assistant setup", icon: Bot },
    ],
  },
  {
    title: "AUTOMATION & MAINTENANCE",
    items: [
      { key: "automation", label: "Global automation", desc: "Automate repetitive task workflows and status transitions", icon: Zap },
      { key: "trash", label: "Trash", desc: "Restore soft-deleted projects and review deletion history", icon: Trash2, adminOnly: true },
    ],
  },
];

const FIELD_TYPES = ["text", "textarea", "number", "decimal", "email", "phone", "date", "datetime", "dropdown", "multiselect", "checkbox", "radio", "url", "user", "team", "project", "issue", "sprint", "version"];

const Inner = () => {
  const ctx = usePm();
  const [params, setParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const section = params.get("section") || "general";
  const canEdit = ctx.canWs("pm.settings");

  useEffect(() => {
    pm.settings().then(setSettings).catch((err) => ctx.fail(err));
  }, [ctx]);

  const save = async (patch) => {
    try {
      setSettings({ ...(await pm.updateSettings(patch)), templateVariables: settings.templateVariables, aiProviderConfigured: settings.aiProviderConfigured });
      ctx.notify("Settings saved");
      ctx.reload();
    } catch (err) {
      ctx.fail(err, "Not saved");
    }
  };

  if (!ctx.canWs("pm.settings") && !ctx.me.isAdmin) return <Alert severity="warning">Project settings need the PM settings permission. Ask a PM admin.</Alert>;

  const filteredGroups = PM_SETTING_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => !item.adminOnly || ctx.me.isAdmin),
  })).filter((g) => g.items.length > 0);

  const allItems = filteredGroups.flatMap((g) => g.items);
  const currentItem = allItems.find((i) => i.key === section) || allItems[0];
  const CurrentIcon = currentItem?.icon || Settings;

  return (
    <>
      <PageHeader
        icon={Settings}
        title="Project Management Settings"
        subtitle="Workspace-wide configuration for projects, workflows, issue types, permissions, and automation"
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "260px minmax(0,1fr)" }, gap: 2.5, alignItems: "start" }}>
        <GlassCard
          sx={{
            p: 1.5,
            height: { md: "calc(100vh - 175px)" },
            maxHeight: { md: "calc(100vh - 175px)" },
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 transparent",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#cbd5e1" },
          }}
        >
          <List dense disablePadding>
            {filteredGroups.map((g, gIdx) => (
              <Box key={g.title} sx={{ mb: gIdx === filteredGroups.length - 1 ? 0 : 2 }}>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                    color: "#98A2B3",
                    px: 1.5,
                    py: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  {g.title}
                </Typography>
                {g.items.map((item) => {
                  const isSelected = section === item.key;
                  const Icon = item.icon;
                  return (
                    <ListItemButton
                      key={item.key}
                      selected={isSelected}
                      onClick={() => setParams({ section: item.key }, { replace: true })}
                      sx={{
                        borderRadius: "8px",
                        my: 0.25,
                        px: 1.5,
                        py: 0.75,
                        "&.Mui-selected": {
                          bgcolor: "rgba(99, 102, 241, 0.12)",
                          color: "rgb(99, 102, 241)",
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, width: "100%" }}>
                        {Icon && <Icon size={16} color={isSelected ? "rgb(99, 102, 241)" : "#667085"} style={{ flexShrink: 0 }} />}
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: TYPE.small,
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? "rgb(99, 102, 241)" : "#344054",
                            noWrap: true,
                          }}
                        />
                      </Stack>
                    </ListItemButton>
                  );
                })}
              </Box>
            ))}
          </List>
        </GlassCard>
        <Box
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            height: { md: "calc(100vh - 175px)" },
            maxHeight: { md: "calc(100vh - 175px)" },
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#cbd5e1" },
          }}
        >
          {section === "general" && settings && <General settings={settings} save={save} canEdit={canEdit} />}
          {section === "types" && (
            <ConfigList path="issue-types" title="Issue types" subtitle="Level 0 = epic-like, 1 = standard, 2 = sub-task. Allowed parents define the hierarchy." canEdit={canEdit}
              defaults={{ level: 1, color: "#2E90FA", icon: "task", isActive: true, allowedParentTypeIds: [] }}
              columns={[["name", "Type", (v, r) => <Stack direction="row" spacing={1} alignItems="center"><TypeIcon typeId={r._id} size={14} /><span>{v}</span></Stack>], ["level", "Level"], ["allowedParentTypeIds", "Parents", (v, r, c) => (v || []).map((id) => c.typeById.get(String(id))?.name).join(", ") || "—"], ["requiresParent", "Needs parent", bool], ["isActive", "Active", bool]]}
              fields={[{ key: "name", label: "Name" }, { key: "description", label: "Description" }, { key: "level", label: "Level", type: "select", options: [[0, "0 · Epic"], [1, "1 · Standard"], [2, "2 · Sub-task"]].map(([value, label]) => ({ value, label })) },
                { key: "icon", label: "Icon", type: "select", options: ["epic", "story", "task", "bug", "subtask"].map((v) => ({ value: v, label: v })) }, { key: "color", label: "Colour", type: "color" },
                { key: "allowedParentTypeIds", label: "Allowed parent types", type: "multiselect", options: (c, e) => c.issueTypes.filter((t) => t._id !== e._id).map((t) => ({ value: String(t._id), label: t.name })) },
                { key: "requiresParent", label: "Requires a parent", type: "bool" }, { key: "order", label: "Order", type: "number" }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "statuses" && (
            <ConfigList path="statuses" title="Statuses" subtitle="The category drives progress, boards and reports (To do / In progress / Done)." canEdit={canEdit} defaults={{ category: "todo", color: "#98A2B3", isActive: true }}
              columns={[["color", "", Swatch], ["name", "Name"], ["category", "Category"], ["isActive", "Active", bool]]}
              fields={[{ key: "name", label: "Name" }, { key: "category", label: "Category", type: "select", options: [["todo", "To do"], ["in_progress", "In progress"], ["done", "Done"]].map(([value, label]) => ({ value, label })) }, { key: "color", label: "Colour", type: "color" }, { key: "order", label: "Order", type: "number" }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "priorities" && (
            <ConfigList path="priorities" title="Priorities" canEdit={canEdit} defaults={{ level: 3, color: "#F79009", isActive: true }}
              columns={[["color", "", Swatch], ["name", "Name"], ["level", "Level"], ["isDefault", "Default", (v) => (v ? "Yes" : "")], ["isActive", "Active", bool]]}
              fields={[{ key: "name", label: "Name" }, { key: "level", label: "Level (higher = more urgent)", type: "number" }, { key: "color", label: "Colour", type: "color" }, { key: "isDefault", label: "Default for new issues", type: "bool" }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "links" && (
            <ConfigList path="link-types" title="Link types" subtitle="Dependency link types are drawn on the roadmap and count for 'blocked' health." canEdit={canEdit} defaults={{ isActive: true, isDependency: false }}
              columns={[["name", "Name"], ["outward", "Outward"], ["inward", "Inward"], ["isDependency", "Dependency", (v) => (v ? "Yes" : "")], ["isActive", "Active", bool]]}
              fields={[{ key: "name", label: "Name" }, { key: "outward", label: "Outward text (A … B)", help: "e.g. blocks" }, { key: "inward", label: "Inward text", help: "e.g. is blocked by" }, { key: "isDependency", label: "Dependency", type: "bool" }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "projectTypes" && (
            <ConfigList path="project-types" title="Project types" canEdit={canEdit} defaults={{ color: "#6366F1", isActive: true }}
              columns={[["color", "", Swatch], ["name", "Name"], ["description", "Description"], ["isActive", "Active", bool]]}
              fields={[{ key: "name", label: "Name" }, { key: "description", label: "Description" }, { key: "color", label: "Colour", type: "color" }, { key: "order", label: "Order", type: "number" }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "fields" && (
            <ConfigList path="custom-fields" title="Custom fields" subtitle="Scope a field to projects and issue types; internal fields are hidden from client roles." canEdit={canEdit}
              defaults={{ type: "text", visibility: "all", isActive: true, projectIds: [], issueTypeIds: [], options: [] }}
              columns={[["label", "Label"], ["key", "Key"], ["type", "Type"], ["required", "Required", (v) => (v ? "Yes" : "")], ["visibility", "Visibility"], ["isActive", "Active", bool]]}
              transform={(b) => ({ ...b, options: (b.options || []).map((o) => o.trim()).filter(Boolean) })}
              fields={[{ key: "label", label: "Label" }, { key: "key", label: "Key (lower_snake_case)", create: true }, { key: "type", label: "Type", type: "select", create: true, options: FIELD_TYPES.map((v) => ({ value: v, label: v })) },
                { key: "options", label: "Options", type: "options", hidden: (e) => !["dropdown", "multiselect", "radio"].includes(e.type) }, { key: "required", label: "Required", type: "bool" },
                { key: "visibility", label: "Visibility", type: "select", options: [{ value: "all", label: "Everyone" }, { value: "internal", label: "Internal (hidden from client roles)" }] },
                { key: "issueTypeIds", label: "Issue types (empty = all)", type: "multiselect", options: (c) => c.issueTypes.map((t) => ({ value: String(t._id), label: t.name })) },
                { key: "order", label: "Order", type: "number" }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "roles" && <Roles canEdit={ctx.me.isAdmin || canEdit} />}
          {section === "people" && <Access isAdmin={ctx.me.isAdmin} />}
          {section === "templates" && (
            <ConfigList path="templates" noCreate title="Project templates" subtitle="Templates set a new project's issue types, workflow, board and estimation. Edit a project's workflow after creating it." canEdit={canEdit}
              columns={[["name", "Name"], ["description", "Description"], ["board", "Board", (b) => b?.type], ["estimation", "Estimation"], ["isActive", "Active", bool]]}
              fields={[{ key: "name", label: "Name" }, { key: "description", label: "Description" }, { key: "estimation", label: "Estimation", type: "select", options: ["points", "hours", "count"].map((v) => ({ value: v, label: v })) }, { key: "isActive", label: "Active", type: "bool" }]} />
          )}
          {section === "notifications" && settings && <Notifications settings={settings} save={save} canEdit={canEdit} />}
          {section === "health" && settings && <HealthAi settings={settings} save={save} canEdit={canEdit} />}
          {section === "automation" && <AutomationRules project={null} />}
          {section === "trash" && (ctx.me.isAdmin ? <Trash /> : <Alert severity="info">Only PM admins can see and restore deleted projects.</Alert>)}
        </Box>
      </Box>
      {!settings && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Loading…</Typography>}
    </>
  );
};

const PmSettings = () => (
  <PmPage>
    <Inner />
  </PmPage>
);

export default PmSettings;
